import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";
import { useRepos, type Repo } from "./useRepos.js";
import { useWorkspace } from "../api/hooks/useWorkspace";
import { CreateRepoModal } from "./CreateRepoModal.js";
import { ConnectRepoModal } from "./ConnectRepoModal.js";
import { Table, THead, TBody, Tr, Th, Td } from "../ui/Table";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { Banner } from "../ui/Banner";
import { PageHeader } from "../ui/PageHeader";
import { repoHome, repoProposals, repoSettings } from "../lib/paths";
import { useTranslation, apiErrorMessage } from "../i18n";

export function ReposPage() {
  const { t } = useTranslation("repos");
  const { t: tErr } = useTranslation("errors");
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const menu = useContextMenu();
  const [showModal, setShowModal] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error } = useRepos(slug);

  // Returning from the GitHub App install callback (?connect=github) reopens the
  // connect modal so the freshly-added connection can be picked.
  useEffect(() => {
    if (searchParams.get("connect") === "github") {
      setShowConnect(true);
      searchParams.delete("connect");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Creating a repo is repo:admin, which the policy grants to owners only.
  const isOwner = useWorkspace(slug).data?.role === "owner";

  const repos = data?.repos ?? [];

  function menuItems(repo: Repo): ContextMenuItem[] {
    // Clone URL mirrors the browser URL — copy the address bar and clone.
    const cloneUrl = window.location.origin + repoHome(slug, repo.slug);
    return [
      { label: t("menu.openRepo"), onClick: () => void navigate(repoHome(slug, repo.slug)) },
      {
        label: t("menu.copyCloneUrl"),
        onClick: () => void navigator.clipboard.writeText(cloneUrl),
      },
    ];
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <PageHeader
        title={t("list.title")}
        description={t("list.description")}
        actions={
          isOwner ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setShowConnect(true)}>
                {t("list.connectExisting")}
              </Button>
              <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
                {t("list.newRepository")}
              </Button>
            </>
          ) : null
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Banner kind="error">{apiErrorMessage(error, tErr)}</Banner>
      ) : repos.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-12 text-center">
          <p className="text-lg font-medium text-slate-200">{t("list.emptyTitle")}</p>
          <p className="mt-1 text-sm text-slate-500">
            {isOwner ? t("list.emptyOwner") : t("list.emptyMember")}
          </p>
          {isOwner && (
            <Button
              variant="primary"
              className="mt-6"
              onClick={() => setShowModal(true)}
            >
              {t("list.newRepository")}
            </Button>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden">
          <Table>
            <THead>
              <Tr>
                <Th>{t("list.colRepository")}</Th>
                <Th>{t("list.colDescription")}</Th>
                <Th>{t("list.colDefaultBranch")}</Th>
                <Th>{t("list.colCreated")}</Th>
                <Th> </Th>
              </Tr>
            </THead>
            <TBody>
              {repos.map((repo) => (
                <Tr key={repo.id} onContextMenu={(e) => menu.open(e, menuItems(repo))}>
                  <Td>
                    <Link
                      to={repoHome(slug, repo.slug)}
                      className="font-medium text-arch-400 hover:text-arch-300 hover:underline"
                    >
                      {repo.slug}
                    </Link>
                  </Td>
                  <Td className="text-slate-400">
                    {repo.description ?? <span className="text-slate-600 italic">—</span>}
                  </Td>
                  <Td>
                    <span className="rounded bg-slate-800 px-2 py-0.5 font-mono text-xs text-slate-300">
                      {repo.defaultBranch}
                    </span>
                  </Td>
                  <Td className="text-slate-400 text-xs">
                    {new Date(repo.createdAt).toLocaleDateString()}
                  </Td>
                  <Td className="text-right text-xs">
                    <Link
                      to={repoProposals(slug, repo.slug)}
                      className="text-slate-400 hover:text-arch-300 hover:underline"
                    >
                      {t("list.changes")}
                    </Link>
                    <span className="px-1.5 text-slate-700">·</span>
                    <Link
                      to={repoSettings(slug, repo.slug)}
                      className="text-slate-400 hover:text-arch-300 hover:underline"
                    >
                      {t("list.settings")}
                    </Link>
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </div>
      )}

      <CreateRepoModal
        open={showModal}
        onClose={() => setShowModal(false)}
        wsSlug={slug}
      />

      <ConnectRepoModal
        open={showConnect}
        onClose={() => setShowConnect(false)}
        wsSlug={slug}
      />

      {menu.node}
    </div>
  );
}
